import React, { useContext, useReducer, useState } from "react";

import { OutcomeQuiz } from "../../../graphql/queries/games/outcome-quiz.js";
import OutcomeQuizQuestion from "./OutcomeQuizQuestion";
import OutcomeQuizSummary from "./OutcomeQuizSummary";
// import FeaturedQuizList from "../quiz/FeaturedQuizList";
import Spinner from "../../Spinner";

import { gaTrackOutcomeQuizEvent } from "../../../helpers/googleAnalytics";
import { Content } from "../../layout/CommonContentContextProvider";
import { QuizStage } from "../_common/types.js";
import { quizFrame } from "../quiz/QuizContainer.css.js";
import GameIntro from "../_common/GameIntro.js";
import QuizPageAdBanner from "../quiz/QuizPageAdBanner.js";
import { App } from "../../../graphql/queries/app-config.js";

interface Props {
    outcomeQuiz: OutcomeQuiz;
    refreshing: boolean;
    pagePath: string;
    onPickVariant: (variantId: string) => void;
    saveResult: (quizId: string, result: number) => void;
    refresh: () => void;
}

// interface InitialSummaries extends OutcomeQuizSummaryContent {
//     totalCount: number;
// }

type SumWeights = number[];

type AnswerReducerAction =
    | {
          type: "sum";
          weights: number[];
      }
    | {
          type: "reset";
      };

type AnswerReducer = (
    prevValue: SumWeights,
    action: AnswerReducerAction,
) => SumWeights;

const handleAnswerSelection = (
    sumValues: SumWeights,
    action: AnswerReducerAction,
) => {
    switch (action.type) {
        case "sum": {
            return sumValues.map((s, i) => s + action.weights[i]);
        }
        case "reset": {
            return sumValues.map(() => 0);
        }
    }
};
const per = (a: number, b: number): string => {
    return b === 0 ? "0%" : `${Math.round(100 * (a / b))}%`;
};
const OutcomeQuizContainer: React.FC<Props> = ({
    outcomeQuiz,
    pagePath,
    onPickVariant,
    saveResult,
    refresh,
    refreshing,
}) => {
    const { langCode } = useContext(Content);

    const [stage, setStage] = useState<QuizStage>("start");
    const { content } = outcomeQuiz;

    const initSumWeights: SumWeights = content.summaries.map(() => 0);

    const [resultWeights, dispatch] = useReducer<AnswerReducer, SumWeights>(
        handleAnswerSelection,
        initSumWeights,
        () => initSumWeights,
    );

    const scrollToTop = () => {
        if (typeof window !== "undefined") {
            window.scrollTo(0, 0);
        }
    };

    if (refreshing) {
        return <Spinner />;
    }

    if (stage === "start") {
        return (
            <>
                <main className={quizFrame}>
                    <GameIntro
                        gameId={outcomeQuiz.id}
                        slug={outcomeQuiz.slug}
                        title={content.title}
                        text={content.text}
                        startButtonText={content.startButton}
                        image={content.image}
                        onStartHandler={() => {
                            setStage(0);
                            scrollToTop();
                            gaTrackOutcomeQuizEvent(
                                "Start",
                                outcomeQuiz.id,
                                langCode,
                            );
                        }}
                        pagePath={pagePath}
                        engagementInfo={outcomeQuiz.engagementInfo}
                        topics={outcomeQuiz.topics}
                        usersCount={outcomeQuiz.usersCount}
                        typename={outcomeQuiz.__typename}
                    />
                </main>
                <QuizPageAdBanner
                    id={outcomeQuiz.id}
                    stage={stage}
                    questionsCount={content.questions.length}
                />
            </>
        );
    }

    if (stage === "result") {
        const { winnerIndex } = resultWeights.reduce(
            (m, v, i) => {
                if (m.maxWeight < v) {
                    m.maxWeight = v;
                    m.winnerIndex = i;
                }
                return m;
            },
            { winnerIndex: 0, maxWeight: 0 },
        );
        saveResult(outcomeQuiz.id, winnerIndex + 1);

        const resultSummary = content.summaries[winnerIndex];
        const percent = per(
            resultSummary.pickedCount || 0,
            outcomeQuiz.usersCount || 0,
        );
        return (
            <>
                <main className={quizFrame}>
                    <OutcomeQuizSummary
                        quizId={outcomeQuiz.id}
                        slug={outcomeQuiz.slug}
                        restartButtonText={content.restartButton}
                        title={resultSummary.title}
                        res={`${winnerIndex + 1}`}
                        text={resultSummary.text.replace("%PERCENT%", percent)}
                        mediaItem={resultSummary.image || content.image}
                        pagePath={pagePath}
                        apps={outcomeQuiz.promoApps.filter(
                            (a): a is App => !!a,
                        )}
                        engagementInfo={outcomeQuiz.engagementInfo}
                        shareMessage={content.shareMessage} // TODO: update message with variable
                        shareBlockTitle={content.shareYourResult || undefined}
                        onRestartHandler={() => {
                            dispatch({ type: "reset" });
                            setStage(0);
                            refresh();
                            scrollToTop();
                            gaTrackOutcomeQuizEvent(
                                "Restart",
                                outcomeQuiz.id,
                                langCode,
                                `${winnerIndex}`,
                            );
                        }}
                        topics={outcomeQuiz.topics}
                    />
                </main>
                {/* <FeaturedQuizList
                    quizId={quiz.id}
                    featuredGames={featuredGames}
                /> */}
                <QuizPageAdBanner
                    id={outcomeQuiz.id}
                    stage={stage}
                    questionsCount={content.questions.length}
                />
            </>
        );
    }
    return (
        <>
            <main className={quizFrame}>
                <OutcomeQuizQuestion
                    key={stage}
                    question={content.questions[stage]}
                    outcomeQuizId={outcomeQuiz.id}
                    stage={stage + 1}
                    fullStage={`${stage + 1} / ${content.questions.length}`}
                    onPickHandler={(variantId: string, weights: number[]) => {
                        onPickVariant(variantId);
                        dispatch({ type: "sum", weights });
                        gaTrackOutcomeQuizEvent(
                            "AnswerQuestionID",
                            outcomeQuiz.id,
                            langCode,
                            variantId,
                        );
                        gaTrackOutcomeQuizEvent(
                            "AnswerStage",
                            outcomeQuiz.id,
                            langCode,
                            `${stage + 1}`,
                        );
                    }}
                    onNextHandler={() => {
                        if (stage === content.questions.length - 1) {
                            setStage("result");
                        } else {
                            setStage(stage + 1);
                        }
                        scrollToTop();
                    }}
                />
            </main>
            <QuizPageAdBanner
                id={outcomeQuiz.id}
                stage={stage}
                questionsCount={content.questions.length}
            />
        </>
    );
};

export default OutcomeQuizContainer;
